import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Globe2 as OrbitIcon, Loader2, Plus, X, Calendar, Target, Lightbulb, AlertTriangle, Layers } from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';
import api from '../utils/api';
import { toast } from 'sonner';

const PROJECT_TYPES = {
  en: [
    'Building a Business',
    'Writing a Book',
    'Organizing an Event',
    'Building a House',
    'Launching a Product',
    'Research Project',
    'Creative Project',
    'Community Project',
    'Personal Challenge Group',
    'Other'
  ],
  tr: [
    'İş Kurma',
    'Kitap Yazma',
    'Etkinlik Düzenleme',
    'Ev İnşa Etme',
    'Ürün Lansmanı',
    'Araştırma Projesi',
    'Yaratıcı Proje',
    'Topluluk Projesi',
    'Kişisel Meydan Okuma Grubu',
    'Diğer'
  ]
};

const CreateOrbit = () => {
  const navigate = useNavigate();
  const { language } = useLanguage();
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1); // 1: Basic Info, 2: Details, 3: Components
  
  const [formData, setFormData] = useState({
    project_name: '',
    project_type: '',
    project_mission: '',
    success_outcome: '',
    core_idea: '',
    key_components: [],
    biggest_challenge: '',
    target_completion_date: ''
  });
  
  const [newComponent, setNewComponent] = useState('');

  const t = {
    en: {
      title: 'Create Orbit',
      step1: 'Basic Info',
      step2: 'Vision & Goals',
      step3: 'Key Components',
      projectName: 'Project Name',
      projectNamePlaceholder: 'e.g., Launch SaaS Product',
      projectType: 'Project Type',
      selectType: 'Select project type...',
      projectMission: 'Project Mission',
      projectMissionPlaceholder: 'Why does this project exist? What problem does it solve?',
      successOutcome: 'Success Outcome',
      successOutcomePlaceholder: 'What does success look like? Be specific.',
      coreIdea: 'Core Idea',
      coreIdeaPlaceholder: 'Describe the project in a few sentences...',
      keyComponents: 'Key Components',
      keyComponentsDesc: 'What major parts or milestones must happen?',
      addComponent: 'Add component...',
      biggestChallenge: 'Biggest Challenge',
      biggestChallengePlaceholder: 'What is the biggest obstacle to overcome?',
      targetDate: 'Target Completion Date',
      back: 'Back',
      next: 'Next',
      create: 'Launch Orbit',
      creating: 'Creating...',
    },
    tr: {
      title: 'Orbit Oluştur',
      step1: 'Temel Bilgiler',
      step2: 'Vizyon ve Hedefler',
      step3: 'Temel Bileşenler',
      projectName: 'Proje Adı',
      projectNamePlaceholder: 'örn., SaaS Ürünü Lansman',
      projectType: 'Proje Türü',
      selectType: 'Proje türü seçin...',
      projectMission: 'Proje Misyonu',
      projectMissionPlaceholder: 'Bu proje neden var? Hangi sorunu çözüyor?',
      successOutcome: 'Başarı Sonucu',
      successOutcomePlaceholder: 'Başarı neye benziyor? Spesifik olun.',
      coreIdea: 'Ana Fikir',
      coreIdeaPlaceholder: 'Projeyi birkaç cümleyle açıklayın...',
      keyComponents: 'Temel Bileşenler',
      keyComponentsDesc: 'Hangi büyük parçalar veya kilometre taşları gerçekleşmeli?',
      addComponent: 'Bileşen ekle...',
      biggestChallenge: 'En Büyük Zorluk',
      biggestChallengePlaceholder: 'Aşılması gereken en büyük engel nedir?',
      targetDate: 'Hedef Tamamlanma Tarihi',
      back: 'Geri',
      next: 'İleri',
      create: 'Orbit\'i Başlat',
      creating: 'Oluşturuluyor...',
    }
  };

  const c = t[language] || t.en;
  const projectTypes = PROJECT_TYPES[language] || PROJECT_TYPES.en;

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addComponent = () => {
    if (newComponent.trim()) {
      setFormData(prev => ({
        ...prev,
        key_components: [...prev.key_components, newComponent.trim()]
      }));
      setNewComponent('');
    }
  };

  const removeComponent = (index) => {
    setFormData(prev => ({
      ...prev,
      key_components: prev.key_components.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = async () => {
    if (!formData.project_name || !formData.project_type || !formData.project_mission) {
      toast.error(language === 'tr' ? 'Lütfen gerekli alanları doldurun' : 'Please fill required fields');
      return;
    }

    try {
      setLoading(true);
      const response = await api.post('/orbit/create', formData);
      toast.success(language === 'tr' ? 'Orbit oluşturuldu!' : 'Orbit created!');
      navigate(`/orbit/${response.data.orbit_id}`);
    } catch (error) {
      console.error('Error creating orbit:', error);
      toast.error(language === 'tr' ? 'Orbit oluşturulamadı' : 'Failed to create Orbit');
    } finally {
      setLoading(false);
    }
  };

  const canProceed = () => {
    if (step === 1) return formData.project_name && formData.project_type;
    if (step === 2) return formData.project_mission && formData.success_outcome;
    return true;
  };

  // Scroll to top on mount and step change
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [step]);

  return (
    <div
      className="min-h-screen px-4 pb-12"
      style={{ position: 'relative', zIndex: 1, paddingTop: 'calc(env(safe-area-inset-top, 0px) + 1.5rem)' }}
    >
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center gap-4 mb-8"
        >
          <button
            onClick={() => step > 1 ? setStep(step - 1) : navigate('/orbit')}
            className="text-white/70 hover:text-white transition-colors"
          >
            <ArrowLeft className="w-6 h-6" />
          </button>
          <div className="flex items-center gap-3">
            <OrbitIcon className="w-7 h-7 text-[#D4AF37]" />
            <h1 className="font-serif text-2xl text-[#F8F5F2]">{c.title}</h1>
          </div>
        </motion.div>

        {/* Progress Steps */}
        <div className="flex items-center justify-center gap-2 mb-8">
          {[1, 2, 3].map((s) => (
            <div
              key={s}
              className={`h-2 rounded-full transition-all ${
                s === step ? 'w-8 bg-[#D4AF37]' : s < step ? 'w-4 bg-[#D4AF37]/50' : 'w-4 bg-white/10'
              }`}
            />
          ))}
        </div>

        {/* Step 1: Basic Info */}
        {step === 1 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <h2 className="font-serif text-xl text-[#F8F5F2] mb-1">{c.step1}</h2>
              <p className="text-[#A1A1AA] text-sm">
                {language === 'tr' ? 'Projenizin temelleri' : 'The foundation of your project'}
              </p>
            </div>

            <div className="glass-panel rounded-2xl p-6 space-y-5">
              {/* Project Name */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  {c.projectName} *
                </label>
                <input
                  type="text"
                  value={formData.project_name}
                  onChange={(e) => handleChange('project_name', e.target.value)}
                  placeholder={c.projectNamePlaceholder}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors"
                  data-testid="project-name-input"
                />
              </div>

              {/* Project Type */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <Layers className="w-4 h-4 text-[#D4AF37]" />
                  {c.projectType} *
                </label>
                <select
                  value={formData.project_type}
                  onChange={(e) => handleChange('project_type', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none transition-colors appearance-none cursor-pointer"
                  data-testid="project-type-select"
                >
                  <option value="" className="bg-[#0a0a0f]">{c.selectType}</option>
                  {projectTypes.map((type) => (
                    <option key={type} value={type} className="bg-[#0a0a0f]">{type}</option>
                  ))}
                </select>
              </div>

              {/* Target Date */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <Calendar className="w-4 h-4 text-[#D4AF37]" />
                  {c.targetDate}
                </label>
                <input
                  type="date"
                  value={formData.target_completion_date}
                  onChange={(e) => handleChange('target_completion_date', e.target.value)}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] focus:border-[#D4AF37]/50 focus:outline-none transition-colors"
                  data-testid="target-date-input"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 2: Vision & Goals */}
        {step === 2 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <h2 className="font-serif text-xl text-[#F8F5F2] mb-1">{c.step2}</h2>
              <p className="text-[#A1A1AA] text-sm">
                {language === 'tr' ? 'Projenizin vizyonunu tanımlayın' : 'Define your project vision'}
              </p>
            </div>

            <div className="glass-panel rounded-2xl p-6 space-y-5">
              {/* Project Mission */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <Lightbulb className="w-4 h-4 text-[#D4AF37]" />
                  {c.projectMission} *
                </label>
                <textarea
                  value={formData.project_mission}
                  onChange={(e) => handleChange('project_mission', e.target.value)}
                  placeholder={c.projectMissionPlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors resize-none"
                  data-testid="project-mission-input"
                />
              </div>

              {/* Success Outcome */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <Target className="w-4 h-4 text-[#D4AF37]" />
                  {c.successOutcome} *
                </label>
                <textarea
                  value={formData.success_outcome}
                  onChange={(e) => handleChange('success_outcome', e.target.value)}
                  placeholder={c.successOutcomePlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors resize-none"
                  data-testid="success-outcome-input"
                />
              </div>

              {/* Core Idea */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  {c.coreIdea}
                </label>
                <textarea
                  value={formData.core_idea}
                  onChange={(e) => handleChange('core_idea', e.target.value)}
                  placeholder={c.coreIdeaPlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors resize-none"
                  data-testid="core-idea-input"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Step 3: Key Components */}
        {step === 3 && (
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            <div className="text-center mb-6">
              <h2 className="font-serif text-xl text-[#F8F5F2] mb-1">{c.step3}</h2>
              <p className="text-[#A1A1AA] text-sm">{c.keyComponentsDesc}</p>
            </div>

            <div className="glass-panel rounded-2xl p-6 space-y-5">
              {/* Key Components */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-3">
                  <Layers className="w-4 h-4 text-[#D4AF37]" />
                  {c.keyComponents}
                </label>
                
                {/* Component List */}
                <div className="space-y-2 mb-4">
                  {formData.key_components.map((comp, idx) => (
                    <div
                      key={idx}
                      className="flex items-center gap-2 px-4 py-2 rounded-lg bg-white/5 border border-white/10"
                    >
                      <span className="flex-1 text-[#F8F5F2] text-sm">{comp}</span>
                      <button
                        onClick={() => removeComponent(idx)}
                        className="text-[#A1A1AA] hover:text-[#E24A4A] transition-colors"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                </div>

                {/* Add Component */}
                <div className="flex gap-2">
                  <input
                    type="text"
                    value={newComponent}
                    onChange={(e) => setNewComponent(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addComponent())}
                    placeholder={c.addComponent}
                    className="flex-1 px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors"
                    data-testid="add-component-input"
                  />
                  <button
                    onClick={addComponent}
                    className="px-4 py-3 rounded-xl bg-[#D4AF37]/20 text-[#D4AF37] hover:bg-[#D4AF37]/30 transition-colors"
                  >
                    <Plus className="w-5 h-5" />
                  </button>
                </div>
              </div>

              {/* Biggest Challenge */}
              <div>
                <label className="flex items-center gap-2 text-sm text-[#A1A1AA] mb-2">
                  <AlertTriangle className="w-4 h-4 text-[#D4AF37]" />
                  {c.biggestChallenge}
                </label>
                <textarea
                  value={formData.biggest_challenge}
                  onChange={(e) => handleChange('biggest_challenge', e.target.value)}
                  placeholder={c.biggestChallengePlaceholder}
                  rows={3}
                  className="w-full px-4 py-3 rounded-xl bg-white/5 border border-white/10 text-[#F8F5F2] placeholder-[#6B7280] focus:border-[#D4AF37]/50 focus:outline-none transition-colors resize-none"
                  data-testid="biggest-challenge-input"
                />
              </div>
            </div>
          </motion.div>
        )}

        {/* Navigation Buttons */}
        <div className="flex gap-3 mt-8">
          {step > 1 && (
            <button
              onClick={() => setStep(step - 1)}
              className="flex-1 py-3.5 rounded-xl border border-[#D4AF37]/30 text-[#D4AF37] font-semibold hover:bg-[#D4AF37]/10 transition-all"
            >
              {c.back}
            </button>
          )}
          
          {step < 3 ? (
            <button
              onClick={() => setStep(step + 1)}
              disabled={!canProceed()}
              className="flex-1 py-3.5 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {c.next}
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={loading}
              className="flex-1 py-3.5 rounded-xl bg-[#D4AF37] text-[#030305] font-semibold hover:bg-[#D4AF37]/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              data-testid="create-orbit-submit"
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {c.creating}
                </>
              ) : (
                <>
                  <OrbitIcon className="w-5 h-5" />
                  {c.create}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default CreateOrbit;
